/**
 * URL utilities to ensure correct domain is used.
 * Fixes issues when REACT_APP_BACKEND_URL has wrong domain.
 */

/**
 * Get the correct API base URL.
 * Uses window.location.origin if the env variable seems wrong.
 */
export const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // If we're in a browser, check if the env URL matches current domain
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    
    // List of known old/wrong domains that should be replaced
    const wrongDomains = [
      'sacred-journeys',
      'emergent.host',
      'preview.emergentagent.com',
      'localhost:3000',
    ];
    
    // Check if env URL contains any wrong domain
    const hasWrongDomain = wrongDomains.some(d => envUrl.includes(d));
    
    // If env URL has wrong domain and we're on production, use current origin
    if (hasWrongDomain && currentOrigin.includes('demarretours.com')) {
      return currentOrigin;
    }
    
    // If env URL is empty or has wrong domain, use current origin
    if (!envUrl || hasWrongDomain) {
      // For preview environments, trust the env variable
      if (currentOrigin.includes('preview.emergentagent.com')) {
        return envUrl || currentOrigin;
      }
      return currentOrigin;
    }
  }
  
  return envUrl;
};

/**
 * Build a full URL for a file path.
 * Handles relative paths like /api/uploads/...
 */
export const buildFileUrl = (filePath) => {
  if (!filePath) return '';
  
  // If it's already a full URL with correct domain, return as-is
  if (filePath.startsWith('http')) {
    // Check if it's using a wrong domain
    const wrongDomains = ['sacred-journeys', 'emergent.host'];
    const hasWrongDomain = wrongDomains.some(d => filePath.includes(d));
    
    if (hasWrongDomain) {
      // Extract the path and rebuild with correct base
      const pathMatch = filePath.match(/\/api\/.+$/);
      if (pathMatch) {
        return `${getApiBaseUrl()}${pathMatch[0]}`;
      }
    }
    
    return filePath;
  }
  
  // For relative paths, prepend the base URL
  const base = getApiBaseUrl();
  const path = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return `${base}${path}`;
};

/**
 * Get the API URL for axios calls.
 */
export const getApiUrl = () => {
  return `${getApiBaseUrl()}/api`;
};

export default {
  getApiBaseUrl,
  buildFileUrl,
  getApiUrl,
};
